import './TemplateManager.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import Toast from '../Common/Toast';
import useToast from '../Common/useToast';
import { STAGE_WIDTH, STAGE_HEIGHT } from './chapterStage';
import {
    TEMPLATE_TYPES, getTemplates, createTemplate, updateTemplate, deleteTemplate,
} from '../../api/templates';

// Template block snapshots store raw pixel left/top/width/height in the design-stage coordinate
// space (see chapterStage.js) -- the SAME space the live canvas in Chapter.jsx lays blocks out in
// and scales to fit. A card preview is far narrower than a real canvas, so the ghost blocks are
// laid out at full stage size here too and uniformly transform: scale()d down to fit; sharing the
// constants is what guarantees a preview is a faithful miniature of the real page rather than a
// separate layout that happens to look similar.
const PREVIEW_STAGE_WIDTH = STAGE_WIDTH;
const PREVIEW_STAGE_HEIGHT = STAGE_HEIGHT;

const emptyTemplateDraft = {
    mode: 'new', // 'new' = create a template | 'override' = overwrite an existing one
    overrideTargetId: null, // which existing template 'override' mode writes to
    template_name: '',
    template_type: 'Normal',
    backgroundFile: undefined, // undefined = untouched, null = cleared, File = new upload
    backgroundPreviewUrl: '',
    template_background_type: 'image',
    includeCurrentBlocks: true,
};

const isVideoFile = (file) => !!file && file.type?.startsWith('video/');

// Mirrors Chapter.jsx's local block shape -> the snake_case payload the block endpoints expect,
// so a stored template is a straight pass-through at apply time with no second translation layer
// to keep in sync. Ids are deliberately dropped: a template is a detached snapshot, and reusing
// real row ids would make applying it overwrite the blocks it was captured from.
const blockToSnapshot = (block, index) => ({
    block_type: block.type === 'text-block' ? 'text' : (block.type || 'card'),
    title: block.title || '',
    text: block.text || '',
    title_size: block.titleSize ?? 24,
    text_size: block.textSize ?? 16,
    title_font: block.titleFont || block.font || 'Inter',
    title_bold: !!(block.titleBold ?? block.bold),
    title_layout: block.titleLayout || block.layout || 'left',
    title_color: block.titleColor || block.color || '#0f172a',
    title_toggle: block.titleToggle ?? true,
    text_font: block.textFont || block.font || 'Inter',
    text_bold: !!(block.textBold ?? block.bold),
    text_layout: block.textLayout || block.layout || 'left',
    text_color: block.textColor || block.color || '#0f172a',
    background_color: (block.backgroundColor || '#ffffff').slice(0, 7),
    background_opacity: block.backgroundOpacity ?? 1,
    background_toggle_width: !!block.backgroundToggleWidth,
    background_corners: block.backgroundCorners || 'Round',
    background_text: block.backgroundText || '',
    background_text_toggle: block.backgroundTextToggle ?? true,
    background_text_size: block.backgroundTextSize ?? 16,
    background_text_font: block.backgroundTextFont || block.font || 'Inter',
    background_text_bold: !!(block.backgroundTextBold ?? block.bold),
    background_text_layout: block.backgroundTextLayout || block.layout || 'left',
    background_text_color: block.backgroundTextColor || block.color || '#0f172a',
    font: block.font || 'Inter',
    bold: !!block.bold,
    layout: block.layout || 'left',
    color: block.color || '#0f172a',
    // Media is carried by reference (URL_media), never by copying the file: the file belongs to
    // the LessonPageBlock row it was uploaded for. See the caveat in TemplateManager's docs --
    // deleting that original block revokes the file and blanks it in any template using it.
    URL_media: block.mediaUrl || block.URL_media || null,
    width: Math.round(block.width ?? 700),
    height: Math.round(block.height ?? 200),
    left_position: Math.round(block.left ?? 0),
    top_position: Math.round(block.top ?? 0),
    order: block.order ?? index,
});

/**
 * Template toolbar + modals for the chapter editor.
 *
 * Two deliberately separate concerns (the Template.html prototype conflated both under
 * "thumbnail", which is why its naming reads oddly):
 *   - Page background: the image/video behind ONE page's blocks. Lives on LessonPage.
 *   - Template:        a reusable preset (background + a snapshot of blocks) applicable to ANY
 *                      page. Lives on the Template model.
 *
 * Everything that touches chapterData is delegated to Chapter.jsx through callbacks, so this
 * component never mutates lesson content directly (which would fight Chapter's undo/redo and
 * unsaved-changes tracking).
 */
function TemplateManager({
    isEditing = false,
    isUseTemplateOpen = false,
    onCloseUseTemplate,
    currentPageBlocks = [],
    currentPageBackgroundUrl = '',
    currentPageBackgroundType = 'image',
    onApplyTemplate,
    onSetPageBackground,
    onClearPageBackground,
    slideshowSlides = [],
    slideshowStartAt = null,
    onSlideshowGoTo,
    onSlideshowActiveChange,
    onToast,
}) {
    const { t } = useTranslation();
    // TEMPLATE_TYPES ('Introduction'/'Normal'/'Conclusion') are the backend's fixed enum values --
    // stored as-is in template_type and used as CSS/data keys (thumb-category-badge, data-type),
    // never translated. This maps one to its display label only.
    const categoryLabel = useCallback((type) => t(`templateManager.categories.${type}`, type), [t]);

    const [templates, setTemplates] = useState([]);
    const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
    const [activeTypeTab, setActiveTypeTab] = useState('Introduction');

    const [templateDraft, setTemplateDraft] = useState(null); // null = save/create modal closed
    const [isSavingTemplate, setIsSavingTemplate] = useState(false);

    const [pageBackgroundModal, setPageBackgroundModal] = useState(null); // { file, previewUrl, type }

    // Editor-mode-only: lets an admin tuck the toolbar away when it covers the canvas. Reset on
    // leaving editor mode, otherwise the collapse tab (which only renders while editing) would be
    // gone and there'd be no way to bring the toolbar back.
    const [isToolbarCollapsed, setIsToolbarCollapsed] = useState(false);
    useEffect(() => {
        if (!isEditing) setIsToolbarCollapsed(false);
    }, [isEditing]);

    const [confirmDialog, setConfirmDialog] = useState(null); // { message, danger, onConfirm }

    // ---------------------------------------------------------------- toast / confirm

    // A host page that already shows toasts (Chapter.jsx) passes onToast and owns the
    // only visible instance; the local one below stays unrendered in that case. Both
    // toasts are fixed at bottom-centre, so two live instances would overlap exactly.
    const { toast: localToast, showToast: showLocalToast } = useToast();
    const showToast = useCallback((message, variant = 'success') => {
        if (typeof onToast === 'function') onToast(message, variant);
        else showLocalToast(message, variant);
        // Both branches are stable references (useToast memoizes showToast, and the
        // parent's onToast comes from the same hook), so showToast is stable too --
        // which the useCallbacks listing it as a dependency rely on.
    }, [onToast, showLocalToast]);

    const askConfirm = useCallback((message, onConfirm, danger = false) => {
        setConfirmDialog({ message, onConfirm, danger });
    }, []);

    const closeConfirmDialog = useCallback((confirmed) => {
        const pending = confirmDialog;
        setConfirmDialog(null);
        if (confirmed && pending?.onConfirm) pending.onConfirm();
    }, [confirmDialog]);

    // ---------------------------------------------------------------- templates data

    const loadTemplates = useCallback(async () => {
        setIsLoadingTemplates(true);
        try {
            const rows = await getTemplates();
            setTemplates(Array.isArray(rows) ? rows : []);
        } catch (error) {
            console.error('Error loading templates:', error);
            showToast(t('templateManager.toastLoadTemplatesFailed'), 'error');
        } finally {
            setIsLoadingTemplates(false);
        }
    }, [showToast, t]);

    // Only fetched when a modal that lists templates is actually opened -- they're irrelevant to
    // the ~99% of chapter-editing time both are closed. The save modal needs them too now, for its
    // "override an existing template" mode.
    useEffect(() => {
        if (isUseTemplateOpen) loadTemplates();
    }, [isUseTemplateOpen, loadTemplates]);

    const templatesByType = useMemo(() => {
        const grouped = {};
        TEMPLATE_TYPES.forEach((type) => { grouped[type] = []; });
        templates.forEach((template) => {
            const type = TEMPLATE_TYPES.includes(template.template_type) ? template.template_type : 'Normal';
            grouped[type].push(template);
        });
        return grouped;
    }, [templates]);

    const visibleTemplates = templatesByType[activeTypeTab] || [];

    // ---------------------------------------------------------------- save / edit template

    const revokePreview = (url) => {
        if (url && url.startsWith('blob:')) URL.revokeObjectURL(url);
    };

    const openSaveTemplateModal = useCallback(() => {
        setTemplateDraft({
            ...emptyTemplateDraft,
            // Whatever background the page is already showing is offered as the template's
            // background by default (by reference -- see the note in handleSaveTemplate).
            backgroundPreviewUrl: currentPageBackgroundUrl || '',
            template_background_type: currentPageBackgroundType || 'image',
        });
        // Override mode lists existing templates, so they have to be loaded even though the picker
        // modal (the other place that loads them) may never have been opened this session.
        loadTemplates();
    }, [currentPageBackgroundUrl, currentPageBackgroundType, loadTemplates]);

    const closeSaveTemplateModal = useCallback(() => {
        setTemplateDraft((draft) => {
            revokePreview(draft?.backgroundPreviewUrl);
            return null;
        });
    }, []);

    const handleTemplateBackgroundPick = (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setTemplateDraft((draft) => {
            revokePreview(draft?.backgroundPreviewUrl);
            return {
                ...draft,
                backgroundFile: file,
                backgroundPreviewUrl: URL.createObjectURL(file),
                template_background_type: isVideoFile(file) ? 'video' : 'image',
            };
        });
    };

    const handleTemplateBackgroundClear = () => {
        setTemplateDraft((draft) => {
            revokePreview(draft?.backgroundPreviewUrl);
            return { ...draft, backgroundFile: null, backgroundPreviewUrl: '', template_background_type: 'image' };
        });
    };

    const overrideTarget = useMemo(
        () => templates.find((item) => item.id === templateDraft?.overrideTargetId) || null,
        [templates, templateDraft?.overrideTargetId]
    );

    const handleSaveTemplate = async () => {
        if (!templateDraft || isSavingTemplate) return;
        const isOverride = templateDraft.mode === 'override';

        if (isOverride && !overrideTarget) {
            showToast(t('templateManager.toastPickOverrideTarget'), 'error');
            return;
        }
        const name = templateDraft.template_name.trim();
        if (!name) {
            showToast(t('templateManager.toastNameRequired'), 'error');
            return;
        }

        setIsSavingTemplate(true);
        try {
            const payload = isOverride
                // An override keeps the target's TYPE (it stays in the same tab) but the name is
                // editable, so it's always sent -- prefilled from the target, so leaving it alone
                // is a no-op.
                ? { template_name: name }
                : {
                    template_name: name,
                    template_type: templateDraft.template_type,
                    template_background_type: templateDraft.template_background_type || 'image',
                };

            // Only send a background when it actually changed: a File uploads, an explicit null
            // clears it, and `undefined` leaves whatever the row already has untouched.
            if (templateDraft.backgroundFile instanceof File) {
                payload.template_background_image = templateDraft.backgroundFile;
                payload.template_background_type = templateDraft.template_background_type || 'image';
            } else if (templateDraft.backgroundFile === null) {
                payload.template_background_image = null;
                payload.template_background_type = 'image';
            }

            // A new template always captures the page. An override only replaces the target's
            // stored blocks when the admin ticks the box -- otherwise its blocks are left alone
            // (letting you swap just the background of an existing template).
            if (!isOverride || templateDraft.includeCurrentBlocks) {
                payload.template_content = currentPageBlocks.map(blockToSnapshot);
            }

            const saved = isOverride
                ? await updateTemplate(overrideTarget.id, payload)
                : await createTemplate(payload);

            setTemplates((prev) => (
                isOverride
                    ? prev.map((item) => (item.id === saved.id ? saved : item))
                    : [...prev, saved]
            ));
            setActiveTypeTab(saved.template_type || 'Normal');
            closeSaveTemplateModal();
            showToast(isOverride
                ? t('templateManager.toastTemplateOverridden', { name: saved.template_name })
                : t('templateManager.toastTemplateSaved', { name: saved.template_name, type: categoryLabel(saved.template_type) }));
        } catch (error) {
            console.error('Error saving template:', error);
            showToast(t('templateManager.toastSaveTemplateFailed'), 'error');
        } finally {
            setIsSavingTemplate(false);
        }
    };

    const handleDeleteTemplate = (template) => {
        askConfirm(t('templateManager.confirmDeleteTemplate', { name: template.template_name }), async () => {
            try {
                await deleteTemplate(template.id);
                setTemplates((prev) => prev.filter((item) => item.id !== template.id));
                showToast(t('templateManager.toastTemplateDeleted'));
            } catch (error) {
                console.error('Error deleting template:', error);
                showToast(t('templateManager.toastDeleteTemplateFailed'), 'error');
            }
        }, true);
    };

    const handleUseTemplate = (template) => {
        const blockCount = (template.template_content || []).length;
        const existingCount = currentPageBlocks.length;
        const message = existingCount > 0
            ? t('templateManager.confirmApplyTemplateReplace', { name: template.template_name, blockCount, existingCount })
            : t('templateManager.confirmApplyTemplate', { name: template.template_name });

        askConfirm(message, () => {
            onApplyTemplate?.({
                blocks: template.template_content || [],
                backgroundUrl: template.template_background_url || '',
                backgroundType: template.template_background_type || 'image',
            });
            onCloseUseTemplate?.();
            showToast(t('templateManager.toastTemplateApplied', { name: template.template_name }));
        });
    };

    // ---------------------------------------------------------------- page background

    const openPageBackgroundModal = useCallback(() => {
        setPageBackgroundModal({
            file: undefined,
            previewUrl: currentPageBackgroundUrl || '',
            type: currentPageBackgroundType || 'image',
        });
    }, [currentPageBackgroundUrl, currentPageBackgroundType]);

    const closePageBackgroundModal = useCallback(() => {
        setPageBackgroundModal((state) => {
            revokePreview(state?.previewUrl);
            return null;
        });
    }, []);

    const handlePageBackgroundPick = (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setPageBackgroundModal((state) => {
            revokePreview(state?.previewUrl);
            return {
                ...state,
                file,
                previewUrl: URL.createObjectURL(file),
                type: isVideoFile(file) ? 'video' : 'image',
            };
        });
    };

    const handleApplyPageBackground = async () => {
        if (!pageBackgroundModal) return;
        if (!(pageBackgroundModal.file instanceof File)) {
            closePageBackgroundModal();
            return;
        }
        try {
            await onSetPageBackground?.({ file: pageBackgroundModal.file, type: pageBackgroundModal.type });
            closePageBackgroundModal();
            showToast(t('templateManager.toastPageBackgroundUpdated'));
        } catch (error) {
            console.error('Error setting page background:', error);
            showToast(t('templateManager.toastSetPageBackgroundFailed'), 'error');
        }
    };

    const handleClearPageBackground = () => {
        askConfirm(t('templateManager.confirmRemoveBackground'), async () => {
            try {
                await onClearPageBackground?.();
                closePageBackgroundModal();
                showToast(t('templateManager.toastPageBackgroundRemoved'));
            } catch (error) {
                console.error('Error clearing page background:', error);
                showToast(t('templateManager.toastRemovePageBackgroundFailed'), 'error');
            }
        }, true);
    };

    // ---------------------------------------------------------------- slideshow

    const [slideshowPos, setSlideshowPos] = useState(null); // null = not in slideshow

    const startSlideshow = useCallback(() => {
        if (!slideshowSlides.length) {
            showToast(t('templateManager.toastNothingToPresent'), 'error');
            return;
        }
        const startIdx = slideshowStartAt
            ? slideshowSlides.findIndex((s) => s.lessonIdx === slideshowStartAt.lessonIdx && s.pageIdx === slideshowStartAt.pageIdx)
            : 0;
        const pos = startIdx >= 0 ? startIdx : 0;
        setSlideshowPos(pos);
        const slide = slideshowSlides[pos];
        if (slide) onSlideshowGoTo?.(slide.lessonIdx, slide.pageIdx);

        // Slides are authored landscape, so on a phone we ask for landscape outright. The lock
        // only works from inside fullscreen, hence the chaining. It's progressive enhancement:
        // iOS Safari has no Screen Orientation lock at all, so the failure path is silent and the
        // portrait hint in the overlay tells the user to rotate manually instead.
        const fullscreenRequest = document.documentElement.requestFullscreen?.();
        if (fullscreenRequest?.then) {
            fullscreenRequest
                .then(() => window.screen?.orientation?.lock?.('landscape'))
                .catch(() => {});
        }
    }, [slideshowSlides, slideshowStartAt, onSlideshowGoTo, showToast, t]);

    const exitSlideshow = useCallback(() => {
        setSlideshowPos(null);
        // Release the lock before leaving fullscreen -- the browser drops it automatically on
        // exit, but unlocking explicitly avoids leaving the page pinned to landscape if the
        // fullscreen exit is what failed.
        try { window.screen?.orientation?.unlock?.(); } catch (e) { /* unsupported: ignore */ }
        if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    }, []);

    const goToSlide = useCallback((pos) => {
        const slide = slideshowSlides[pos];
        if (!slide) return;
        setSlideshowPos(pos);
        onSlideshowGoTo?.(slide.lessonIdx, slide.pageIdx);
    }, [slideshowSlides, onSlideshowGoTo]);

    // Kept in a ref so the keydown/fullscreen listeners below can stay mounted once instead of
    // re-binding on every slide change.
    const slideshowPosRef = useRef(slideshowPos);
    useEffect(() => { slideshowPosRef.current = slideshowPos; }, [slideshowPos]);

    const isSlideshowActive = slideshowPos !== null;

    // Chapter.jsx needs this to suspend its editing-oriented scroll clamping while presenting.
    useEffect(() => {
        onSlideshowActiveChange?.(isSlideshowActive);
    }, [isSlideshowActive, onSlideshowActiveChange]);

    // "P" toggles presenting. Mounted permanently (unlike the navigation keys below, which only
    // exist during a slideshow) since it also has to work as the way IN.
    const isAnyModalOpen = !!(templateDraft || pageBackgroundModal || confirmDialog) || isUseTemplateOpen;
    useEffect(() => {
        const handleToggleKey = (e) => {
            if (e.key !== 'p' && e.key !== 'P') return;
            // Ctrl/Cmd+P is print and Alt+P may be a system shortcut -- never hijack those.
            if (e.ctrlKey || e.metaKey || e.altKey) return;
            // The chapter editor is full of contentEditable blocks and text inputs; typing a "p"
            // in one of them must never launch a presentation.
            const el = document.activeElement;
            if (el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName))) return;
            // A dialog is its own context -- let Escape/its buttons drive it instead.
            if (isAnyModalOpen) return;

            e.preventDefault();
            if (slideshowPosRef.current !== null) exitSlideshow();
            else startSlideshow();
        };
        document.addEventListener('keydown', handleToggleKey);
        return () => document.removeEventListener('keydown', handleToggleKey);
    }, [isAnyModalOpen, startSlideshow, exitSlideshow]);

    useEffect(() => {
        if (!isSlideshowActive) return;

        document.body.classList.add('slideshow-mode');

        const handleKeyDown = (e) => {
            if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') {
                e.preventDefault();
                const next = (slideshowPosRef.current ?? 0) + 1;
                if (next < slideshowSlides.length) goToSlide(next);
            } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
                e.preventDefault();
                const prev = (slideshowPosRef.current ?? 0) - 1;
                if (prev >= 0) goToSlide(prev);
            } else if (e.key === 'Escape') {
                exitSlideshow();
            }
        };

        // Leaving fullscreen by any route (Esc handled by the browser, F11, window chrome) should
        // drop out of slideshow too, otherwise the page stays stuck behind the overlay.
        const handleFullscreenChange = () => {
            if (!document.fullscreenElement) exitSlideshow();
        };

        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => {
            document.body.classList.remove('slideshow-mode');
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, [isSlideshowActive, slideshowSlides.length, goToSlide, exitSlideshow]);

    // ---------------------------------------------------------------- render helpers

    const renderTemplatePreview = (template) => {
        const blocks = template.template_content || [];
        const backgroundUrl = template.template_background_url || '';
        const isVideo = template.template_background_type === 'video';
        const hasBackground = !!backgroundUrl;

        return (
            <div className="tm-preview-stage-wrap">
                <div
                    className="thumb-scaler tm-preview-stage"
                    style={{
                        width: `${PREVIEW_STAGE_WIDTH}px`,
                        height: `${PREVIEW_STAGE_HEIGHT}px`,
                        backgroundImage: !isVideo && backgroundUrl ? `url('${backgroundUrl}')` : undefined,
                    }}
                >
                    {isVideo && backgroundUrl && (
                        <video className="tm-preview-bg-video" src={backgroundUrl} muted loop playsInline autoPlay />
                    )}
                    {blocks.map((block, idx) => (
                        <div
                            key={idx}
                            className="tm-preview-block"
                            style={{
                                left: `${block.left_position ?? 0}px`,
                                top: `${block.top_position ?? 0}px`,
                                width: `${block.width ?? 700}px`,
                                height: `${block.height ?? 200}px`,
                                zIndex: block.block_type === 'background' ? 1 : 10,
                            }}
                        >
                            {renderPreviewBlockInner(block, hasBackground)}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderPreviewBlockInner = (block, hasBackground) => {
        const type = block.block_type;
        // Over a photo the real canvas relies on the admin having picked readable colours; in the
        // tiny preview a white-on-dark fallback keeps ghost text legible either way.
        const titleColor = block.title_color || (hasBackground ? '#ffffff' : '#0f172a');
        const textColor = block.text_color || (hasBackground ? '#ffffff' : '#334155');
        const textShadow = hasBackground ? '0 2px 10px rgba(0,0,0,0.8)' : 'none';

        if (type === 'image' || type === 'video') {
            return block.URL_media
                ? (type === 'video'
                    ? <video src={block.URL_media} muted playsInline className="tm-preview-media" />
                    : <img src={block.URL_media} alt="" className="tm-preview-media" />)
                : <div className="tm-preview-placeholder" />;
        }

        if (type === 'background') {
            return (
                <div
                    className="tm-preview-shape"
                    style={{
                        background: block.background_color || '#dbeafe',
                        opacity: block.background_opacity ?? 1,
                        borderRadius: block.background_corners === 'Square' ? '0px' : '28px',
                    }}
                >
                    {block.background_text_toggle !== false && block.background_text && (
                        <span style={{
                            fontSize: `${block.background_text_size ?? 16}px`,
                            fontFamily: block.background_text_font || 'Inter',
                            fontWeight: block.background_text_bold ? 700 : 400,
                            textAlign: block.background_text_layout || 'left',
                            color: block.background_text_color || '#0f172a',
                        }}>
                            {block.background_text}
                        </span>
                    )}
                </div>
            );
        }

        if (type === 'quiz') {
            return <div className="tm-preview-quiz">{t('templateManager.quizPreviewLabel')}</div>;
        }

        const showTitle = block.title_toggle !== false && !!block.title;
        return (
            <div
                className={type === 'card' ? 'tm-preview-card' : undefined}
                style={type === 'card' ? {
                    background: block.background_color || '#ffffff',
                    opacity: block.background_opacity ?? 1,
                } : undefined}
            >
                {showTitle && (
                    <h3 style={{
                        margin: '0 0 10px 0',
                        fontSize: `${block.title_size ?? 24}px`,
                        fontFamily: block.title_font || 'Inter',
                        fontWeight: block.title_bold ? 700 : 400,
                        textAlign: block.title_layout || 'left',
                        color: type === 'card' ? (block.title_color || '#0f172a') : titleColor,
                        textShadow: type === 'card' ? 'none' : textShadow,
                        lineHeight: 1.15,
                    }}>
                        {block.title}
                    </h3>
                )}
                {!!block.text && (
                    <p style={{
                        margin: 0,
                        fontSize: `${block.text_size ?? 16}px`,
                        fontFamily: block.text_font || 'Inter',
                        fontWeight: block.text_bold ? 700 : 400,
                        textAlign: block.text_layout || 'left',
                        color: type === 'card' ? (block.text_color || '#334155') : textColor,
                        textShadow: type === 'card' ? 'none' : textShadow,
                        lineHeight: 1.5,
                    }}>
                        {block.text}
                    </p>
                )}
            </div>
        );
    };

    // ---------------------------------------------------------------- render

    return (
        <div className="template-manager">
            {/* Labels are wrapped so the toolbar can collapse to icons-only on narrow viewports
                (see the media query in TemplateManager.css) instead of overflowing. */}
            <div className={`template-toolbar${isToolbarCollapsed ? ' collapsed' : ''}`}>
                {/* Collapse tab, editor-mode only -- it's there to get the pill out of the way of
                    the canvas while laying blocks out, which only matters while editing. */}
                {isEditing && (
                    <button
                        className="template-toolbar-collapse"
                        onClick={() => setIsToolbarCollapsed((current) => !current)}
                        title={isToolbarCollapsed ? t('templateManager.showToolbar') : t('templateManager.hideToolbar')}
                        aria-expanded={!isToolbarCollapsed}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9 18l6-6-6-6" />
                        </svg>
                    </button>
                )}

                <div className="template-toolbar-actions">
                    {isEditing && (
                        <>
                            <button
                                className="template-preview-btn"
                                onClick={openPageBackgroundModal}
                                title={t('templateManager.pageBackgroundTooltip')}
                            >
                                <span className="tpl-btn-icon" aria-hidden="true">🖼️</span>
                                <span className="tpl-btn-label">{t('templateManager.pageBackground')}</span>
                            </button>
                            <button
                                className="template-preview-btn"
                                onClick={openSaveTemplateModal}
                                title={t('templateManager.saveAsTemplateTooltip')}
                            >
                                <span className="tpl-btn-icon" aria-hidden="true">💾</span>
                                <span className="tpl-btn-label">{t('templateManager.saveAsTemplate')}</span>
                            </button>
                            <span className="template-toolbar-divider" aria-hidden="true" />
                        </>
                    )}
                    <button
                        className="template-preview-btn template-preview-btn-present"
                        onClick={startSlideshow}
                        title={t('templateManager.presentTooltip')}
                    >
                        <span className="tpl-btn-icon" aria-hidden="true">▶</span>
                        <span className="tpl-btn-label">{t('templateManager.slideshow')}</span>
                        <kbd className="tpl-btn-kbd">P</kbd>
                    </button>
                </div>
            </div>

            {/* One-click background removal, without going through the Page Background modal.
                Only rendered when there is actually a background to remove. */}
            {isEditing && !!currentPageBackgroundUrl && (
                <button
                    id="removeBackgroundBtn"
                    title={t('templateManager.removeBackgroundTooltip')}
                    onClick={handleClearPageBackground}
                >
                    ✕
                </button>
            )}

            {/* ------------------------------------------------ Page background modal */}
            {pageBackgroundModal && (
                <div id="modalPageBackground" className="tm-modal-overlay" onClick={closePageBackgroundModal}>
                    <div className="tm-modal-box" style={{ width: '600px' }} onClick={(e) => e.stopPropagation()}>
                        <h2 style={{ marginTop: 0 }}>{t('templateManager.pageBackground')}</h2>
                        <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '12px' }}>
                            {t('templateManager.pageBackgroundModalDesc')}
                        </p>

                        <div className="tm-info-note">
                            <Trans i18nKey="templateManager.recommendedSizeNote" components={{ strong: <strong /> }} />
                        </div>

                        <input
                            type="file"
                            id="pageBackgroundInput"
                            accept="image/*,video/*"
                            className="login-file"
                            onChange={handlePageBackgroundPick}
                        />

                        <div className="tm-bg-preview">
                            {pageBackgroundModal.previewUrl ? (
                                pageBackgroundModal.type === 'video' ? (
                                    <video src={pageBackgroundModal.previewUrl} muted loop autoPlay playsInline />
                                ) : (
                                    <img src={pageBackgroundModal.previewUrl} alt={t('templateManager.pageBackgroundPreviewAlt')} />
                                )
                            ) : (
                                <span className="tm-empty-hint">{t('templateManager.noPageBackgroundYet')}</span>
                            )}
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px', gap: '10px' }}>
                            <button
                                className="tm-btn tm-btn-danger"
                                onClick={handleClearPageBackground}
                                disabled={!currentPageBackgroundUrl}
                            >
                                {t('templateManager.removeBackground')}
                            </button>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button className="tm-btn tm-btn-ghost" onClick={closePageBackgroundModal}>{t('common.cancel')}</button>
                                <button
                                    className="tm-btn tm-btn-primary"
                                    onClick={handleApplyPageBackground}
                                    disabled={!(pageBackgroundModal.file instanceof File)}
                                >
                                    {t('templateManager.applyBackground')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ------------------------------------------------ Use template modal */}
            {isUseTemplateOpen && (
                <div id="modalUseTemplate" className="tm-modal-overlay" onClick={onCloseUseTemplate}>
                    <div className="tm-modal-box" style={{ width: '760px' }} onClick={(e) => e.stopPropagation()}>
                        <h2 style={{ marginTop: 0 }}>{t('templateManager.useATemplate')}</h2>
                        <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '16px' }}>
                            {t('templateManager.useTemplateDesc')}
                        </p>

                        <div id="useTemplateTabs" className="thumb-tab-group">
                            {TEMPLATE_TYPES.map((type) => (
                                <button
                                    key={type}
                                    className={`thumb-tab-btn${activeTypeTab === type ? ' active' : ''}`}
                                    onClick={() => setActiveTypeTab(type)}
                                >
                                    {categoryLabel(type)}
                                    <span className="thumb-tab-count">{(templatesByType[type] || []).length}</span>
                                </button>
                            ))}
                        </div>

                        <div className="thumbnail-grid" id="useTemplateGrid">
                            {isLoadingTemplates ? (
                                <p className="tm-grid-empty">{t('templateManager.loadingTemplates')}</p>
                            ) : visibleTemplates.length === 0 ? (
                                <p className="tm-grid-empty">
                                    {t('templateManager.noTemplatesOfType', { type: categoryLabel(activeTypeTab) })}
                                </p>
                            ) : visibleTemplates.map((template) => (
                                <div className="thumb-card-container" key={template.id}>
                                    {/* The whole card is the "use" affordance now -- the separate
                                        Use/Edit button row is gone: Use duplicated the card click,
                                        and editing moved into the Save-as-Template modal's
                                        override mode. */}
                                    <div
                                        className="thumb-preview-box tm-card-clickable"
                                        title={t('templateManager.applyTemplateTooltip', { name: template.template_name })}
                                        onClick={() => handleUseTemplate(template)}
                                    >
                                        <button
                                            className="thumb-delete-btn"
                                            title={t('templateManager.deleteTemplateTooltip')}
                                            onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(template); }}
                                        >
                                            ✕
                                        </button>
                                        <span className={`thumb-category-badge ${template.template_type}`}>
                                            {categoryLabel(template.template_type)}
                                        </span>
                                        {renderTemplatePreview(template)}
                                        <div className="tm-card-name">{template.template_name}</div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                            <button className="tm-btn tm-btn-ghost" onClick={onCloseUseTemplate}>{t('common.close')}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ------------------------------------------------ Save / edit template modal */}
            {templateDraft && (
                <div id="modalSaveTemplate" className="tm-modal-overlay" onClick={closeSaveTemplateModal}>
                    <div className="tm-modal-box" style={{ width: '560px' }} onClick={(e) => e.stopPropagation()}>
                        <h2 style={{ marginTop: 0 }}>{t('templateManager.saveAsTemplate')}</h2>

                        {/* Two destinations for the same captured page: a brand-new template, or
                            overwriting one that already exists. Replaces the old separate "Edit
                            Template" entry point (the Edit button on each picker card). */}
                        <div className="tm-mode-switch">
                            <button
                                type="button"
                                className={`tm-mode-btn${templateDraft.mode === 'new' ? ' active' : ''}`}
                                onClick={() => setTemplateDraft((draft) => ({ ...draft, mode: 'new' }))}
                            >
                                {t('templateManager.saveModeNew')}
                            </button>
                            <button
                                type="button"
                                className={`tm-mode-btn${templateDraft.mode === 'override' ? ' active' : ''}`}
                                onClick={() => setTemplateDraft((draft) => ({ ...draft, mode: 'override' }))}
                            >
                                {t('templateManager.saveModeOverride')}
                            </button>
                        </div>

                        {templateDraft.mode === 'new' ? (
                            <>
                                <label className="tm-label">{t('templateManager.templateName')}</label>
                                <input
                                    type="text"
                                    className="tm-input"
                                    placeholder={t('templateManager.templateNamePlaceholder')}
                                    value={templateDraft.template_name}
                                    onChange={(e) => setTemplateDraft((draft) => ({ ...draft, template_name: e.target.value }))}
                                />

                                <label className="tm-label">{t('templateManager.templateType')}</label>
                                <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
                                    {TEMPLATE_TYPES.map((type) => (
                                        <button
                                            key={type}
                                            type="button"
                                            className={`thumb-type-btn${templateDraft.template_type === type ? ' selected' : ''}`}
                                            onClick={() => setTemplateDraft((draft) => ({ ...draft, template_type: type }))}
                                        >
                                            {categoryLabel(type)}
                                        </button>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <>
                                <label className="tm-label">{t('templateManager.templateToOverride')}</label>
                                <div className="tm-override-list">
                                    {isLoadingTemplates ? (
                                        <p className="tm-empty-hint" style={{ padding: '18px 0' }}>{t('templateManager.loadingTemplates')}</p>
                                    ) : templates.length === 0 ? (
                                        <p className="tm-empty-hint" style={{ padding: '18px 0' }}>
                                            {t('templateManager.noTemplatesSavedYet')}
                                        </p>
                                    ) : templates.map((template) => (
                                        <label
                                            key={template.id}
                                            className={`tm-override-row${templateDraft.overrideTargetId === template.id ? ' selected' : ''}`}
                                        >
                                            <input
                                                type="radio"
                                                style={{ marginTop: '20px'}}
                                                name="tm_override_target"
                                                checked={templateDraft.overrideTargetId === template.id}
                                                // Prefill the rename field with the target's current
                                                // name so the input starts as a no-op edit.
                                                onChange={() => setTemplateDraft((draft) => ({
                                                    ...draft,
                                                    overrideTargetId: template.id,
                                                    template_name: template.template_name || '',
                                                }))}
                                            />
                                            <span className="tm-override-name">{template.template_name}</span>
                                            <span className={`thumb-category-badge ${template.template_type} tm-override-badge`}>
                                                {categoryLabel(template.template_type)}
                                            </span>
                                            <span className="tm-override-meta">
                                                {t('templateManager.blockCount', { count: (template.template_content || []).length })}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                                {overrideTarget && (
                                    <>
                                        <label className="tm-label">{t('templateManager.templateName')}</label>
                                        <input
                                            type="text"
                                            className="tm-input"
                                            placeholder={overrideTarget.template_name}
                                            value={templateDraft.template_name}
                                            onChange={(e) => setTemplateDraft((draft) => ({ ...draft, template_name: e.target.value }))}
                                        />
                                        <div className="tm-warn-note">
                                            {t('templateManager.overrideWarning', {
                                                name: overrideTarget.template_name,
                                                type: categoryLabel(overrideTarget.template_type),
                                            })}
                                        </div>
                                    </>
                                )}
                            </>
                        )}

                        <label className="tm-label">
                            {templateDraft.mode === 'override'
                                ? t('templateManager.backgroundLabelOverride')
                                : t('templateManager.backgroundLabelOptional')}
                        </label>
                        <input
                            type="file"
                            accept="image/*,video/*"
                            className="login-file"
                            onChange={handleTemplateBackgroundPick}
                        />
                        <div className="tm-bg-preview tm-bg-preview-sm">
                            {templateDraft.backgroundPreviewUrl ? (
                                templateDraft.template_background_type === 'video' ? (
                                    <video src={templateDraft.backgroundPreviewUrl} muted loop autoPlay playsInline />
                                ) : (
                                    <img src={templateDraft.backgroundPreviewUrl} alt={t('templateManager.templateBackgroundPreviewAlt')} />
                                )
                            ) : (
                                <span className="tm-empty-hint">{t('templateManager.noTemplateBackgroundHint')}</span>
                            )}
                        </div>
                        {templateDraft.backgroundPreviewUrl && (
                            <button className="tm-btn tm-btn-ghost tm-btn-sm" onClick={handleTemplateBackgroundClear}>
                                {t('templateManager.clearBackground')}
                            </button>
                        )}

                        {/* A page background that came from another template is a URL, not a file we
                            hold -- it can't be re-uploaded onto this template row, so say so plainly
                            rather than silently saving a template with no background. */}
                        {templateDraft.mode === 'new'
                            && !!currentPageBackgroundUrl
                            && !(templateDraft.backgroundFile instanceof File) && (
                            <div className="tm-warn-note">
                                {t('templateManager.cannotCarryBackgroundWarning')}
                            </div>
                        )}

                        {templateDraft.mode === 'override' ? (
                            <label className="tm-checkbox-row">
                                <input
                                    type="checkbox"
                                    checked={templateDraft.includeCurrentBlocks}
                                    onChange={(e) => setTemplateDraft((draft) => ({ ...draft, includeCurrentBlocks: e.target.checked }))}
                                />
                                <span>
                                    <Trans
                                        i18nKey="templateManager.overrideBlocksCheckboxLabel"
                                        values={{ count: currentPageBlocks.length }}
                                        components={{ strong: <strong /> }}
                                    />
                                </span>
                            </label>
                        ) : (
                            <div className="tm-info-note" style={{ marginTop: '4px' }}>
                                <Trans
                                    i18nKey="templateManager.capturesBlocksNote"
                                    values={{ count: currentPageBlocks.length }}
                                    components={{ strong: <strong /> }}
                                />
                            </div>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                            <button className="tm-btn tm-btn-ghost" onClick={closeSaveTemplateModal}>{t('common.cancel')}</button>
                            <button
                                className="tm-btn tm-btn-primary"
                                onClick={handleSaveTemplate}
                                disabled={isSavingTemplate || (templateDraft.mode === 'override' && !overrideTarget)}
                            >
                                {isSavingTemplate
                                    ? t('templateManager.saving')
                                    : templateDraft.mode === 'override' ? t('templateManager.overrideTemplateButton') : t('templateManager.confirmAndSave')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ------------------------------------------------ Confirm dialog */}
            {confirmDialog && (
                <div id="confirmOverlay" className="confirm-overlay" onClick={() => closeConfirmDialog(false)}>
                    <div className="confirm-box" onClick={(e) => e.stopPropagation()}>
                        <p id="confirmMessage">{confirmDialog.message}</p>
                        <div className="confirm-actions">
                            <button className="confirm-btn no" onClick={() => closeConfirmDialog(false)}>{t('common.cancel')}</button>
                            <button
                                className={`confirm-btn yes${confirmDialog.danger ? ' danger' : ''}`}
                                id="confirmYesBtn"
                                onClick={() => closeConfirmDialog(true)}
                            >
                                {confirmDialog.danger ? t('common.delete') : t('common.confirm')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ------------------------------------------------ Toast
                Only when no host page took it over via onToast (see showToast above). */}
            {typeof onToast !== 'function' && <Toast toast={localToast} />}

            {/* ------------------------------------------------ Slideshow overlay */}
            {isSlideshowActive && (
                <div id="slideshowOverlay" className="tm-slideshow-overlay">
                    {/* Shown by CSS only on a small portrait screen -- the fallback for browsers
                        with no Screen Orientation lock (notably iOS Safari), where the user has to
                        turn the device themselves. Disappears the moment they do. */}
                    <div className="tm-rotate-hint">
                        <span className="tm-rotate-icon" aria-hidden="true">📱</span>
                        <p>{t('templateManager.rotateDeviceHint')}</p>
                    </div>
                    <button id="slideshowExitBtn" className="slideshow-ctrl-btn" onClick={exitSlideshow} title={t('templateManager.exitSlideshowTooltip')}>
                        ✕ {t('templateManager.exit')}
                    </button>
                    <button
                        id="slideshowPrevBtn"
                        className="slideshow-ctrl-btn"
                        onClick={() => goToSlide(slideshowPos - 1)}
                        title={t('templateManager.previousSlideTooltip')}
                        style={{ visibility: slideshowPos === 0 ? 'hidden' : 'visible' }}
                    >
                        ‹
                    </button>
                    <button
                        id="slideshowNextBtn"
                        className="slideshow-ctrl-btn"
                        onClick={() => goToSlide(slideshowPos + 1)}
                        title={t('templateManager.nextSlideTooltip')}
                        style={{ visibility: slideshowPos >= slideshowSlides.length - 1 ? 'hidden' : 'visible' }}
                    >
                        ›
                    </button>
                    <div id="slideshowCounter">{slideshowPos + 1} / {slideshowSlides.length}</div>
                </div>
            )}
        </div>
    );
}

export default TemplateManager;
